import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { clearPermissionCache } from '../utils/permissions';

import '../components/AddUserPage.css';
import { Folders } from 'lucide-react';

const permissionStructure = {
  profile: [
    { name: 'Company', permissions: ['-', '-', 'profile', '-'] }
  ],
  folders: [
    { name: 'Master Folder', permissions: ['masterview', '-', '-', '-'] },
    { name: 'Orders Folder', permissions: ['ordersview', '-', '-', '-'] },
    // dont remove comment it -> leadview is now active in folders
    { name: 'Lead Management', permissions: ['leadview', '-', '-', '-'] },
    { name: 'Weta Folder', permissions: ['wetaorder', '-', '-', '-'] },
    { name: 'Ageing Folder', permissions: ['ageingsearch', '-', '-', '-'] },
    { name: 'Notepad Folder', permissions: ['notepadview', '-', '-', '-'] }
    
  ],
  
  master: [
    { name: 'User', permissions: ['user', 'adduser', 'edituser', '-'] },
    { name: 'Diagnosis', permissions: ['diagnosis', 'adddiagnosis', 'editdiagnosis', 'deletediagnosis'] },
    { name: 'Products', permissions: ['product', 'addproduct', 'editproduct', '-'] },
    { name: 'Category', permissions: ['category', 'addcategory', 'editcategory', '-'] }
  ],
  operation: [
    { name: 'Patient Entry', permissions: ['patient', 'addpatiententry', 'editpatiententry', 'deletepatiententry'] }
  ],
  // Delivery is its own permission category in backend
  delivery: [
    { name: 'Delivery', permissions: ['viewdelivery', 'adddelivery', 'editdelivery', 'deletedelivery'] }
  ],
  report: [
    // Report: enable only Create and Delete checkboxes (no Edit)
    // Column order: [View, Create, Edit, Delete]
    { name: 'Patient', permissions: ['viewreport', 'addreport', '-', 'deletereport'] }
  ],
  excel: [
    // Excel Export: enable only View checkbox (export action)
    // Column order: [View, Create, Edit, Delete]
    { name: 'Excel Export', permissions: ['excelexport', '-', '-', '-'] }
  ],
  // dont remove comment it -> lead permission is now active
  lead: [
    { name: 'Lead', permissions: ['-', '-', '-', 'deleteview'] }
  ],
  invoice: [
    { name: 'Invoice', permissions: ['-', '-', 'invoiceeditview', 'invoicedeleteview'] }
  ]
};

const AddUserPage = () => {
  const [userData, setUserData] = useState({
    user_type: '',
    user_name: '',
    password: '',
    user_branch: ''
  });

  const [permissions, setPermissions] = useState({});
  const [checkAll, setCheckAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState('');


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handlePermissionChange = (section, permissionValue) => {
    setPermissions(prev => {
      const newPermissions = { ...prev };
      if (newPermissions[section]) {
        if (newPermissions[section].includes(permissionValue)) {
          newPermissions[section] = newPermissions[section].filter(p => p !== permissionValue);
        } else {
          newPermissions[section] = [...newPermissions[section], permissionValue];
        }
      } else {
        newPermissions[section] = [permissionValue];
      }
      return newPermissions;
    });
  };

  const handleCheckAll = () => {
    const allPermissions = {};

    Object.entries(permissionStructure).forEach(([section, items]) => {
      allPermissions[section] = [];
      items.forEach(item => {
        item.permissions.forEach(perm => {
          if (perm !== '-') {
            allPermissions[section].push(perm);
          }
        });
      });
    });

    setPermissions(checkAll ? {} : allPermissions);
    setCheckAll(!checkAll);
  };

const renderPermissionRow = (section, item) => {
  const name = item.name;
  const perms = item.permissions;
  const sectionPermissions = permissions[section] || [];

  const labels = ["View", "Create", "Edit", "Delete"];

  return (
    <tr key={`${section}-${name}`}>
      <td data-label="Permission">
        <b>{name}</b>
      </td>

      {perms.map((perm, idx) => {
        const isChecked = sectionPermissions.includes(perm);

        return (
          <td
            key={idx}
            data-label={labels[idx]}
            className="text-center"
          >
            {perm !== "-" ? (
              <input
                type="checkbox"
                className="user-add-checkbox"
                checked={isChecked}
                onChange={() => handlePermissionChange(section, perm)}
              />
            ) : (
              <span>-</span>
            )}
          </td>
        );
      })}
    </tr>
  );
};

  const getAuthToken = () => localStorage.getItem('session_token');

  const fetchBranches = async () => {
    try {
      const token = getAuthToken();
      const url = token
        ? `https://newerp.awasthivitality.com/api/branch/show?token=${encodeURIComponent(token)}`
        : 'https://newerp.awasthivitality.com/api/branch/show';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        const formatted = result.data.map(branch => ({
          id: branch.branch_id,
          name: branch.branch_name,
        }));
        setBranches(formatted);
      } else {
        throw new Error('Failed to fetch branches');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load branches');
    }
  };
  useEffect(() => {
    fetchBranches();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Ensure permissions always has all sections, even if empty
      // dont remove comment it -> lead was here before
      const finalPermissions = {
        profile: permissions.profile || [],
        folders: permissions.folders || [],
        master: permissions.master || [],
        operation: permissions.operation || [],
        delivery: permissions.delivery || [],
        report: permissions.report || [],
        excel: permissions.excel || [],
        lead: permissions.lead || [],
        invoice: permissions.invoice || []
      };

      // Prepare data in the exact format expected by the API
      const formData = {
        user_type: userData.user_type,
        user_name: userData.user_name,
        password: userData.password,
        user_branch: userData.user_branch,
        permission: finalPermissions
      };

      console.log('Sending data to API:', formData);
      console.log('Permission breakdown:', {
        profile: finalPermissions.profile.length,
        folders: finalPermissions.folders.length,
        master: finalPermissions.master.length,
        operation: finalPermissions.operation.length,
        delivery: finalPermissions.delivery.length,
        report: finalPermissions.report.length,
        excel: finalPermissions.excel.length
      });

      const token = localStorage.getItem('session_token');
      const url = `https://newerp.awasthivitality.com/api/user/create${token ? `?token=${encodeURIComponent(token)}` : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      console.log('API Response:', result);

      if (response.ok && result.success) {
        toast.success('User created successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        // Reset form
        setUserData({ user_type: '', user_name: '', password: '' });
        setPermissions({});
        setCheckAll(false);

        // Clear permissions cache to force refresh (in case current user's permissions were updated)
        clearPermissionCache();
        window.dispatchEvent(new CustomEvent('permissionsUpdated'));
      } else {
        throw new Error(result.message || 'Failed to create user');
      }

    } catch (error) {
      console.error('Error creating user:', error);
      setMessage('Error: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-add-container">

      <h2>Information</h2>

      {message && (
        <div className={`user-add-message ${messageType === 'success' ? 'success' : 'error'}`}>
          <span className="closebtn" onClick={() => setMessage('')}>&times;</span>
          <strong>{message}</strong>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="user-add-form-section">
          <div>
            <label className="user-add-label">User Type <span className="user-add-required">*</span></label>
            <select
              name="user_type"
              value={userData.user_type}
              onChange={handleInputChange}
              className="user-add-select"
              required
            >
              <option value="">--Select--</option>
              <option value="USER">User</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <div>
            <label className="user-add-label">
              Select User Branch <span className="user-add-required">*</span>
            </label>
            <select
              name="user_branch"
              value={userData.user_branch}
              onChange={handleInputChange}
              className="user-add-select"
              required
            >
              <option value="">--Select Branch--</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="user-add-label">Username <span className="user-add-required">*</span></label>
            <input
              type="text"
              name="user_name"
              value={userData.user_name}
              onChange={handleInputChange}
              className="user-add-input"
              required
            />
          </div>
          <div>
            <label className="user-add-label">Password <span className="user-add-required">*</span></label>
            <input
              type="password"
              name="password"
              value={userData.password}
              onChange={handleInputChange}
              className="user-add-input"
              required
            />
          </div>
        </div>

        <div className="user-add-permissions-section">
          <label>
            <input
              type="checkbox"
              className="user-add-checkbox"
              checked={checkAll}
              onChange={handleCheckAll}
            />{' '}
            Check All
          </label>

          {Object.entries(permissionStructure).map(([section, items]) => (
            <div key={section} className="user-add-permission-block">
              <h3>{section.charAt(0).toUpperCase() + section.slice(1)}</h3>
              <table className="user-add-table">
                <thead>
                  <tr>
                    <th>Permissions</th>
                    <th>View</th>
                    <th>Create</th>
                    <th>Edit</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => renderPermissionRow(section, item))}
                </tbody>
              </table>
            </div>
          ))}
          <button
            type="submit"
            className="user-add-submit-btn"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add'}
          </button>
        </div>


      </form>
      <ToastContainer />

    </div>
  );
};

export { permissionStructure };
export default AddUserPage;